import type { UseGetManyHookValue } from 'react-admin';
import { idFrom, not } from '../../../utils';
import type { BillingItemResource } from '../../../Entities/BillingItem';
import type { DefinedQueryObserverResult } from '@tanstack/react-query';
import { isNil } from 'lodash';

const APPROVED_FOR_INVOICING = '6';

type ValidationResult =
  DefinedQueryObserverResult<BillingItemResource[], Error> | string | undefined;

export function validateBillingItems(
  billingItems: UseGetManyHookValue<BillingItemResource, Error>,
): ValidationResult {
  if (billingItems.isPending || billingItems.isLoading) {
    return;
  }

  if (billingItems.error) {
    return billingItems.error.message;
  }

  if (billingItems.data.length === 0) {
    return 'No billing items selected';
  }

  const merchantId = billingItems.data.map((item) => idFrom(item, 'merchant')).find(not(isNil));

  if (isNil(merchantId)) {
    return 'No merchant found for selected billing items';
  }

  if (!billingItems.data.every((item) => idFrom(item, 'merchant') === merchantId)) {
    return 'All selected billing items must belong to the same merchant';
  }

  const allApproved = billingItems.data.every(
    (item) => idFrom(item, 'billing_item_status') === APPROVED_FOR_INVOICING,
  );

  if (!allApproved) {
    return 'Not all billing items are approved for invoicing';
  }

  return billingItems as DefinedQueryObserverResult<BillingItemResource[], Error>;
}
